import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import * as Actions from 'app/store/actions';
import { getCameraList } from 'app/services/camera';
import CameraListingTable from './CameraListingTable';
import AppliedFiltersBar from '@shared/Reports/AppliedFiltersBar';
import EditCameraModal from './EditCameraModal';
import CameraStatusBubble from './CameraStatusBubble';
import StatusPill from 'shared/components/StatusPill';
import GradientButton from 'shared/components/GradientButton';
import EditYellowIcon from 'shared/icons/EditYellowIcon';
import DeleteRedGradIcon from 'shared/icons/DeleteRedGradIcon';
import CameraListingFiltersBar from './CameraListingFiltersBar';
import qs from 'qs';
import { Avatar, InputAdornment, TextField } from '@material-ui/core';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import DeleteCameraModal from './DeleteCameraModal';
import EditCameraStatusModal from './EditCameraStatusModal';
import FuseAnimate from '@/@fuse/core/FuseAnimate';

const CameraListing = () => {
	const [filters, setFilters] = useState({
		page: 1,
		search: '',
		company_id: [],
		school_id: [],
		room_id: [],
	});
	const [loading, setLoading] = useState(false);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [rows, setRows] = useState([]);
	const [totalResults, setTotalResults] = useState(0);
	const dispatch = useDispatch();
	const cache = useRef({});
	const prevFilters = useRef(filters);
	const [selectedCamera, setSelectedCamera] = useState(null);
	const [editOpen, setEditOpen] = useState(false);
	const [searchTimeout, setSearchTimeout] = useState(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [deleteCameraOpen, setDeleteCameraOpen] = useState(false);
	const [editCameraStatusOpen, setEditCameraStatusOpen] = useState(false);

	const serializeFilters = (filters) => {
		return qs.stringify(filters, { arrayFormat: 'brackets' });
	};

	const generateQueryParams = (filters) => {
		return serializeFilters({
			page: filters.page,
			search: filters.search || '',
			company_id: filters.company_id?.length > 0 ? filters.company_id.map((item) => item.value) : [],
			school_id: filters.school_id?.length > 0 ? filters.school_id.map((item) => item.value) : [],
			room_id: filters.room_id?.length > 0 ? filters.room_id.map((item) => item.value) : [],
			dir: filters.dir,
			sort: filters.sort,
		});
	};

	// Helper function to update filters and reset page to 1 if any filter other than page changes
	const updateFilters = (newFilters) => {
		const filtersChanged = Object.keys(newFilters).some(
			(key) => key !== 'page' && newFilters[key] !== filters[key]
		);
		if (filtersChanged) {
			setFilters({ ...newFilters, page: 1 });
		} else {
			setFilters(newFilters);
		}
	};

	useEffect(() => {
		// Clear cache if any filter other than page changes
		const filtersChanged = Object.keys(filters).some(
			(key) => key !== 'page' && filters[key] !== prevFilters.current[key]
		);
		if (filtersChanged) {
			cache.current = {};
		}
		prevFilters.current = filters;

		setLoading(true);
		const queryParams = generateQueryParams(filters);

		// Check if the results for the current page are already in the cache
		if (cache.current[queryParams]) {
			const cachedData = cache.current[queryParams];
			setRows(cachedData.data);
			setTotalResults(cachedData.total);
			setLoading(false);
		} else {
			getCameraList(queryParams)
				.then((res) => {
					setRows(res.data.data);
					setTotalResults(res.data.meta.total);
					// Store the results in the cache
					cache.current[queryParams] = {
						data: res.data.data,
						total: res.data.meta.total,
					};
				})
				.catch((error) => {
					console.error('API Error:', error);
					dispatch(
						Actions.showMessage({
							message: error?.response?.data?.message || 'Failed to fetch cameras',
							variant: 'error',
						})
					);
				})
				.finally(() => {
					setLoading(false);
				});
		}
	}, [filters]);

	const handleCloseDetails = () => {
		setEditOpen(false);
		setSelectedCamera(null);
	};

	const handleOpenEditCamera = (camera) => {
		setSelectedCamera(camera);
		setEditOpen(true);
	};

	const columns = [
		{
			id: 'room_name',
			label: 'Room Name',
			render: (row) => {
				return (
					<div className="flex items-center gap-4">
						<Avatar src={row.room.thumb} />
						<span className="text-14 font-bold">{row.room_name}</span>
					</div>
				);
			},
			widthClass: 'min-w-256',
		},

		{
			id: 'status',
			label: 'Status',
			render: (row) => <CameraStatusBubble status={row.active} />,
			widthClass: 'min-w-200',
		},

		{
			id: 'device_id',
			label: 'Device ID',

			render: (row) => {
				return <span>{row.camera_id}</span>;
			},
			widthClass: 'min-w-200',
		},
		{
			id: 'facelink_id',
			label: 'Facelink™',
			render: (row) => {
				return (
					<div className="flex items-center">
						<StatusPill
							status={row.facelink ? 'Visible' : 'Hidden'}
							variant={row.facelink ? 'success' : 'error'}
							widthClass="min-w-[60px]"
						/>
					</div>
				);
			},
			widthClass: 'min-w-96',
		},
		{
			id: 'actions',
			label: 'Actions',
			render: (row) => (
				<div className="flex items-center gap-[30px]">
					<GradientButton
						altBorder
						onClick={() => {
							setSelectedCamera(row);
							setEditCameraStatusOpen(true);
						}}
						size="small"
						variant={row.active ? 'secondary' : 'light-blue'}
						width="140px"
					>
						{row.active ? 'Disconnect' : 'Connect'}
					</GradientButton>
					<div className="flex items-center gap-[10px]">
						<button onClick={() => handleOpenEditCamera(row)} type="button">
							<EditYellowIcon className="size-24" />
						</button>
						<button
							onClick={() => {
								setSelectedCamera(row);
								setDeleteCameraOpen(true);
							}}
							type="button"
						>
							<DeleteRedGradIcon className="size-24" />
						</button>
					</div>
				</div>
			),
		},
	];

	const handleSearchChange = (e) => {
		const value = e.target.value;
		setSearchQuery(value);

		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		const timeoutId = setTimeout(() => {
			updateFilters({ ...filters, search: value });
		}, 125); // 125ms debounce

		setSearchTimeout(timeoutId);
	};

	const handleRefreshList = () => {
		cache.current = {};
		setFilters({ ...filters });
	};

	return (
		<FuseAnimate animation="transition.slideLeftIn" duration={600}>
			<div className="">
				<div className="px-24 py-20">
					<div className="flex items-start justify-between lg:gap-32 flex-col lg:flex-row">
						<div className="flex flex-col gap-[40px]">
							<h1 className="text-18 font-bold">Camera Management</h1>
							<p className="lg:text-16 xl:text-20">
								View camera details, status, and manage camera connections. Edit or delete cameras as
								needed.
							</p>
						</div>
						<div className=" self-end">
							<div className="pb-8">
								<TextField
									InputProps={{
										style: {
											marginRight: '24px',
										},
										endAdornment: (
											<InputAdornment
												disablePointerEvents
												position="end"
												style={{ marginLeft: '24px' }}
											>
												<MagnifyingGlassIcon className="size-20" />
											</InputAdornment>
										),
									}}
									className="border-none"
									fullWidth
									id="search"
									label="Search"
									margin="none"
									name="searchQuery"
									onChange={handleSearchChange}
									size="small"
									value={searchQuery}
								/>
							</div>
						</div>
					</div>
				</div>
				<CameraListingFiltersBar filters={filters} setFilters={updateFilters} />
				<div className="border-white border-t-1 border-b-1">
					{/* add empty array to hideFilters to force all filters to be shown except page */}
					<AppliedFiltersBar filters={filters} hideFilters={[]} setFilters={updateFilters} />
				</div>
				<div className="bg-white">
					<div>
						<CameraListingTable
							columns={columns}
							data={rows}
							filters={filters}
							loading={loading}
							page={filters.page}
							rowsPerPage={rowsPerPage}
							setFilters={updateFilters}
							setPage={(page) => updateFilters({ ...filters, page })}
							setRowsPerPage={setRowsPerPage}
							totalResults={totalResults}
						/>
					</div>
				</div>
				<EditCameraModal
					camera={selectedCamera}
					onClose={handleCloseDetails}
					onSuccess={handleRefreshList}
					open={editOpen}
				/>
				<DeleteCameraModal
					camera={selectedCamera}
					onClose={() => setDeleteCameraOpen(false)}
					onSuccess={handleRefreshList}
					open={deleteCameraOpen}
				/>
				<EditCameraStatusModal
					camera={selectedCamera}
					onClose={() => setEditCameraStatusOpen(false)}
					onSuccess={handleRefreshList}
					open={editCameraStatusOpen}
				/>
			</div>
		</FuseAnimate>
	);
};

export default CameraListing;
